import React from 'react';

export default function FacebookFeed({ compact = false, className = '' }) {
  const facebookUrl = "https://www.facebook.com/vissenbjergbageri.dk/";
  const iframeSrc = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(facebookUrl)}&tabs=timeline&width=500&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId`;

  if (compact) {
    return (
      <div className={`overflow-hidden rounded-2xl border border-gold-brown/20 bg-white shadow-xl ${className}`}>
        <div className="bg-chocolate px-6 py-4 text-cream flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white">
              <i className="fa-brands fa-facebook-f text-base" />
            </div>
            <div>
              <h3 className="font-serif text-base font-bold">Vissenbjerg Bageri på Facebook</h3>
              <p className="text-xs text-cream/70">Følg med i nyheder og dagstilbud</p>
            </div>
          </div>
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-gold-brown/30 hover:bg-gold-brown px-3 py-1 text-xs font-semibold text-cream transition-colors"
          >
            <span>Åbn side</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
          </a>
        </div>
        <div className="flex justify-center p-4 bg-beige/20 min-h-[500px]">
          <iframe
            src={iframeSrc}
            width="500"
            height="550"
            style={{ border: 'none', overflow: 'hidden', maxWidth: '100%' }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen={true}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title="Vissenbjerg Bageri Facebook Feed"
            className="w-full max-w-[500px] rounded-lg shadow-sm bg-white"
          />
        </div>
      </div>
    );
  }

  return (
    <section className={`mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 ${className}`}>
      <div className="relative overflow-hidden rounded-3xl bg-white border border-gold-brown/15 shadow-xl">
        {/* Background accent */}
        <div className="absolute top-0 right-0 -mt-16 -mr-16 h-64 w-64 rounded-full bg-gold-brown/5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 h-64 w-64 rounded-full bg-chocolate/5 blur-3xl pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-6 sm:p-10 lg:p-12">
          {/* Left Column: Info & CTA */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1877F2]/10 px-4 py-1.5 text-xs font-semibold text-[#1877F2]">
              <i className="fa-brands fa-facebook text-sm" />
              <span>Følg os på Facebook</span>
            </div>

            <div className="space-y-3">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-chocolate leading-tight">
                Se hvad der sker i bageriet lige nu
              </h2>
              <p className="font-serif text-lg text-gold-brown italic">
                Få de seneste opdateringer, friskbagte nyheder og dagstilbud direkte i dit feed.
              </p>
            </div>

            <p className="text-chocolate/80 text-base leading-relaxed">
              Vi deler løbende billeder af vores friskbagte surdejs- og stenovnsbrød, overdådige kager, særlige højtidsbestillinger og hyggelige glimt fra hverdagen i Vestergade.
            </p>

            {/* Feature Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-beige/30 border border-gold-brown/10">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold-brown/20 text-gold-brown">
                  <i className="fa-solid fa-cake-candles text-sm" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-chocolate">Kageinspiration</h4>
                  <p className="text-xs text-chocolate/70">Se de nyeste kagekreationer</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-beige/30 border border-gold-brown/10">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold-brown/20 text-gold-brown">
                  <i className="fa-solid fa-bread-slice text-sm" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-chocolate">Dagens Brød</h4>
                  <p className="text-xs text-chocolate/70">Hold øje med friskbagt nyt</p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-2">
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1877F2] hover:bg-[#166fe5] px-6 py-3.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200 group"
              >
                <i className="fa-brands fa-facebook text-lg" />
                <span>Besøg vores Facebook-side</span>
                <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right Column: Interactive Facebook Page Plugin Iframe */}
          <div className="lg:col-span-6 flex justify-center w-full">
            <div className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl border border-gold-brown/20 p-2 sm:p-3 bg-gradient-to-b from-beige/20 to-white">
              <div className="rounded-xl overflow-hidden shadow-inner flex justify-center bg-white min-h-[580px]">
                <iframe
                  src={iframeSrc}
                  width="500"
                  height="600"
                  style={{ border: 'none', overflow: 'hidden', width: '100%', maxWidth: '500px' }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen={true}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Vissenbjerg Bageri Facebook Feed"
                  className="w-full h-[600px] rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
